import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const ServicesOffering = () => {
  const navigate = useNavigate();

  const services = [
    {
      id: 'live',
      icon: 'Video',
      title: 'Live Webinars',
      subtitle: 'Interactive Sessions',
      price: '$139',
      period: 'per session',
      badge: 'Most Popular',
      color: 'blue',
      gradient: 'from-blue-600 to-indigo-600',
      features: [
        { name: 'Live Q&A', included: true, highlight: true },
        // { name: 'CPE Credits', included: true, highlight: true },
        { name: 'Recording Access', included: true, highlight: false },
        { name: 'Certificate', included: true, highlight: false },
        { name: 'Expert Interaction', included: true, highlight: true },
      ],
      route: '/webinars/live'
    },
    {
      id: 'recorded',
      icon: 'Play',
      title: 'On-Demand',
      subtitle: '500+ Courses',
      price: '$189',
      period: 'per month',
      badge: 'Best Value',
      color: 'cyan',
      gradient: 'from-cyan-600 to-blue-600',
      features: [
        { name: 'Live Q&A', included: false, highlight: false },
        // { name: 'CPE Credits', included: true, highlight: true },
        { name: 'Recording Access', included: true, highlight: true },
        { name: 'Certificate', included: false, highlight: false },
        { name: 'Expert Interaction', included: false, highlight: false },
      ],
      route: '/library'
    },
    {
      id: 'enterprise',
      icon: 'Award',
      title: 'Enterprise',
      subtitle: 'Custom Solutions',
      price: 'Custom',
      period: 'contact sales',
      badge: 'For Teams',
      color: 'indigo',
      gradient: 'from-indigo-600 to-blue-700',
      features: [
        { name: 'Live Q&A', included: true, highlight: true },
        // { name: 'CPE Credits', included: true, highlight: true },
        { name: 'Recording Access', included: true, highlight: true },
        { name: 'Certificate', included: true, highlight: true },
        { name: 'Expert Interaction', included: true, highlight: true },
      ],
      route: '/enterprise'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/80 backdrop-blur-sm rounded-full border-2 border-blue-200 mb-6">
            <Icon name="Zap" size={18} className="text-blue-600" />
            <span className="text-sm font-bold text-blue-900">DailyRespond Plans</span>
          </div>

          <h2 className="text-5xl md:text-6xl font-black mb-4">
            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 bg-clip-text text-transparent">
              Choose Your Path
            </span>
          </h2>

          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Professional webinar solutions for every team size and budget
          </p>
        </motion.div>

        {/* Comparison Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, idx) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.15 }}
              whileHover={{ y: -12, scale: 1.02 }}
              className={`relative bg-white rounded-3xl shadow-xl overflow-hidden ${
                service.badge === 'Most Popular' ? 'ring-4 ring-blue-500 scale-105' : ''
              }`}
            >
              {/* Badge */}
              {service.badge && (
                <div className="absolute top-0 right-0">
                  <div className={`bg-gradient-to-r ${service.gradient} text-white text-xs font-bold px-4 py-2 rounded-bl-2xl`}>
                    {service.badge}
                  </div>
                </div>
              )}

              {/* Header */}
              <div className={`bg-gradient-to-br ${service.gradient} p-8 text-white`}>
                <motion.div
                  whileHover={{ rotate: 360, scale: 1.15 }}
                  transition={{ duration: 0.6 }}
                  className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4"
                >
                  <Icon name={service.icon} size={32} className="text-white" />
                </motion.div>

                <h3 className="text-2xl font-black mb-2">{service.title}</h3>
                <p className="text-blue-100 text-sm mb-6">{service.subtitle}</p>

                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-black">{service.price}</span>
                  <span className="text-sm text-blue-100">/{service.period}</span>
                </div>
              </div>

              {/* Features */}
              <div className="p-8">
                <ul className="space-y-4 mb-8">
                  {service.features.map((feature, i) => (
                    <li
                      key={i}
                      className={`flex items-center gap-3 ${
                        feature.highlight ? 'font-semibold' : ''
                      }`}
                    >
                      {feature.included ? (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          feature.highlight
                            ? `bg-gradient-to-br ${service.gradient}`
                            : 'bg-blue-100'
                        }`}>
                          <Icon name="Check" size={14} className={feature.highlight ? 'text-white' : 'text-blue-600'} />
                        </div>
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                          <Icon name="X" size={14} className="text-gray-400" />
                        </div>
                      )}
                      <span className={feature.included ? 'text-gray-900' : 'text-gray-400'}>
                        {feature.name}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(service.route)}
                  className={`w-full py-4 px-6 rounded-xl font-bold text-white bg-gradient-to-r ${service.gradient} shadow-lg hover:shadow-2xl transition-all flex items-center justify-center gap-2`}
                >
                  Get Started
                  <Icon name="ArrowRight" size={18} />
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 grid md:grid-cols-3 gap-6"
        >
          {[
            { icon: 'Users', value: '50K+', label: 'Active Learners' },
            { icon: 'Award', value: '99%', label: 'Satisfaction Rate' },
            { icon: 'Clock', value: '24/7', label: 'Support Available' }
          ].map((stat, idx) => (
            <div key={idx} className="bg-white rounded-2xl p-6 text-center shadow-lg">
              <Icon name={stat.icon} size={32} className="text-blue-600 mx-auto mb-3" />
              <div className="text-3xl font-black bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-1">
                {stat.value}
              </div>
              <div className="text-gray-600 font-semibold">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesOffering;
